import type { OrderStatusFilter } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync } from '@solar/ui';

import { api } from '../api';

/**
 * 대시보드 — 레거시 main.blade.php + collections.php getDashboard 파리티.
 *
 * 카드 라벨/카운트는 서버(GET /dashboard)가 역할별로 내려준다. 여기서는
 * 라벨을 신뢰해 그대로 그리고, 이동 가능한 카드만 목록 화면으로 연결한다.
 */

/** 카드 클릭 시 이동할 목록 화면 + 초기 필터. */
export type CardTarget =
  | { menu: 'orders'; status: OrderStatusFilter }
  | { menu: 'as'; state?: number }
  | { menu: 'board_citizen'; responded?: boolean };

/**
 * 카드 key → 목록 화면 매핑.
 *
 * 레거시 링크의 searchStateDoc/searchStateGu 조합은 신규 주문 목록 필터
 * (전체/완료/미완료/취소/미수납)로 표현할 수 없어 해당 카드는 표시만 한다.
 * - my_orders: 레거시도 무필터 목록(/web/order/list)으로 보냈다.
 * - my_as: 레거시 searchType=2 는 배정(state=2) 목록이었다.
 * - qna_pending/qna_done: 콜센터 카드 — 시민회원 QNA 목록의 답변 여부 필터로.
 */
const CARD_TARGETS: Record<string, CardTarget> = {
  as_unassigned: { menu: 'as', state: 1 },
  unassigned_as: { menu: 'as', state: 1 },
  my_as: { menu: 'as', state: 2 },
  my_orders: { menu: 'orders', status: 'all' },
  completed: { menu: 'orders', status: 'completed' },
  qna_pending: { menu: 'board_citizen', responded: false },
  qna_done: { menu: 'board_citizen', responded: true },
};

export function Dashboard({ onNavigate }: { onNavigate: (target: CardTarget) => void }) {
  const dash = useAsync(() => api.dashboard(), []);

  return (
    <div className="admin__body admin__body--single">
      <section className="admin__list">
        {dash.loading && <Spinner />}
        {dash.error && <ErrorBox message={dash.error} onRetry={dash.reload} />}

        {dash.data && dash.data.items.length === 0 && (
          <p className="muted">표시할 대시보드 항목이 없습니다.</p>
        )}

        {dash.data && dash.data.items.length > 0 && (
          <div className="cards">
            {dash.data.items.map((item) => {
              const target = CARD_TARGETS[item.key];
              const body = (
                <>
                  <strong className="card__count">{item.count.toLocaleString()}건</strong>
                  <span className="card__label">{item.label}</span>
                </>
              );
              return target ? (
                <button
                  key={item.key}
                  type="button"
                  className="card card--link"
                  onClick={() => onNavigate(target)}
                >
                  {body}
                </button>
              ) : (
                <div key={item.key} className="card">
                  {body}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
